import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import { usePDFIndividual } from "./EstadoFinanciero";
import { useCurrency } from "../../../../shared/contexts/CurrencyContext";

// ── Helpers ───────────────────────────────────────────────────────────────────
const NIVEL_INDENT = { 1: 0, 2: 16, 3: 28, 4: 40 };
const NIVEL_COLOR  = { 1: "#f97316", 2: "var(--text-primary)", 3: "var(--text-secondary)", 4: "var(--text-muted)" };
const NIVEL_WEIGHT = { 1: 700, 2: 700, 3: 600, 4: 400 };
const NIVEL_BG     = { 1: "rgba(249,115,22,0.06)", 2: "var(--hover-bg)", 3: "transparent", 4: "transparent" };

// ── Fila ──────────────────────────────────────────────────────────────────────
function Fila({ fila, collapsed, onToggle }) {
  const { isUSD, fmtCRC } = useCurrency();
  const indent = NIVEL_INDENT[fila.nivel] ?? 0;
  // Use USD fields when toggled — amounts come directly from backend, no TC conversion needed
  const ba    = Number(isUSD ? (fila.balance_anterior_usd ?? 0) : (fila.balance_anterior ?? 0));
  const deb   = Number(isUSD ? (fila.debitos_usd          ?? 0) : (fila.debitos          ?? 0));
  const cred  = Number(isUSD ? (fila.creditos_usd         ?? 0) : (fila.creditos         ?? 0));
  const final = Number(isUSD ? (fila.saldo_final_usd      ?? 0) : (fila.saldo_final      ?? 0));

  function fmtCol(v) {
    const sign = v < 0 ? "-" : "";
    return `${sign}${fmtCRC(Math.abs(v))}`;
  }

  const zeroFinal = fmtCRC(0);

  return (
    <tr style={{ background: NIVEL_BG[fila.nivel], borderBottom: "1px solid var(--border-card)" }}>
      <td style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "var(--text-muted)",
        whiteSpace: "nowrap", padding: "6px 10px" }}>
        {fila.codigo}
      </td>
      <td style={{ padding: "6px 8px", paddingLeft: indent + 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {fila.tiene_hijos ? (
            <button onClick={() => onToggle(fila.id)}
              style={{ background: "none", border: "none", cursor: "pointer",
                color: "var(--text-muted)", padding: 0, fontSize: "0.65rem", width: 14, flexShrink: 0 }}>
              <i className={`fa-solid fa-chevron-${collapsed.has(fila.id) ? "right" : "down"}`}/>
            </button>
          ) : <span style={{ width: 14, flexShrink: 0 }}/>}
          <span style={{ fontWeight: NIVEL_WEIGHT[fila.nivel], color: NIVEL_COLOR[fila.nivel],
            fontSize: fila.nivel === 1 ? "0.88rem" : "0.83rem" }}>
            {fila.nombre}
          </span>
        </div>
      </td>
      {[
        { val: ba,    zero: "—" },
        { val: deb,   zero: "—", color: "#60a5fa" },
        { val: cred,  zero: "—", color: "#c084fc" },
        { val: final, zero: zeroFinal, color: final < -0.01 ? "#f87171" : final > 0.01 ? "#4ade80" : "var(--text-muted)" },
      ].map((col, i) => (
        <td key={i} className="text-end" style={{ fontFamily: "monospace",
          fontSize: "0.8rem", padding: "6px 12px",
          color: Math.abs(col.val) < 0.01 ? "var(--text-muted)" : (col.color || "var(--text-secondary)"),
          fontWeight: (i === 3 && fila.nivel <= 2) ? 700 : 400 }}>
          {Math.abs(col.val) < 0.01 ? col.zero : fmtCol(col.val)}
        </td>
      ))}
    </tr>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────
export default function BalanceComprobacion() {
  const { t } = useTranslation();
  const { fmtCRC, isUSD, setMultimoneda } = useCurrency();
  const now       = new Date();
  const mesInicio = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const mesFin    = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().split("T")[0];

  const [fechaDesde, setFechaDesde] = useState(mesInicio);
  const [fechaHasta, setFechaHasta] = useState(mesFin);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [soloConMov, setSoloConMov] = useState(true);
  const [collapsed,  setCollapsed]  = useState(new Set());
  const pdfBC = usePDFIndividual(
    '/api/finance/reportes/balance-comprobacion-pdf/',
    'balance_comprobacion.pdf'
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaDesde) qs.set("fecha_desde", fechaDesde);
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res = await apiFetch(`/api/finance/reportes/balance-comprobacion/?${qs}`);
      const d   = await res.json();
      setData(d);
      setMultimoneda(d.multimoneda_activa ?? false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta, setMultimoneda]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filas      = data?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const visibles = filas.filter(f =>
    !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id))
  );

  // Use USD fields when isUSD is active — amounts come directly from backend
  const totalBA   = isUSD
    ? filas.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.balance_anterior_usd ?? 0), 0)
    : filas.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.balance_anterior    ?? 0), 0);
  const totalDeb  = Number(isUSD ? (data?.total_debitos_usd  ?? 0) : (data?.total_debitos  ?? 0));
  const totalCred = Number(isUSD ? (data?.total_creditos_usd ?? 0) : (data?.total_creditos ?? 0));
  const diferencia = isUSD ? (totalDeb - totalCred) : Number(data?.diferencia ?? 0);
  const bal       = data?.balanceado ?? true;

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("reportes.balanceComprobacion")}</span>
      </span>
      <div>
        <h2 className="title mt-2 mb-2">{t("reportes.balanceComprobacion")}</h2>
        <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("reportes.movimientosPeriodo")}</p>
      </div>

      <FiltrosBarra
        fechaDesde={fechaDesde} setFechaDesde={setFechaDesde}
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={fetchData} loading={loading}
        extraButtons={
          <>
            <button onClick={() => pdfBC.generar(`fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`)}
              disabled={pdfBC.loading || loading || !data}
              style={{ padding: "7px 14px", background: "rgba(59,130,246,0.15)",
                border: "1px solid rgba(59,130,246,0.5)", borderRadius: 6,
                color: "#60a5fa", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem",
                opacity: (pdfBC.loading || !data) ? 0.5 : 1 }}>
              {pdfBC.loading
                ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("reportes.generando")}</>
                : <><i className="fa-solid fa-file-pdf" style={{ marginRight: 5 }}/>{t("reportes.pdf")}</>}
            </button>
            {pdfBC.modal}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label: t("reportes.balanceAnterior"), value: totalBA,              color: "var(--text-secondary)", icon: "fa-clock-rotate-left" },
          { label: t("reportes.totalDebitos"),    value: totalDeb,             color: "#60a5fa", icon: "fa-arrow-up" },
          { label: t("reportes.totalCreditos"),   value: totalCred,            color: "#c084fc", icon: "fa-arrow-down" },
          { label: bal ? t("reportes.balanceadoOk") : t("reportes.diferenciaWarn"),
            value: Math.abs(diferencia),
            color: bal ? "#4ade80" : "#f87171",
            icon:  bal ? "fa-scale-balanced" : "fa-triangle-exclamation" },
        ]}/>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={visibles.length} loading={loading}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 130 }}>{t("reportes.codigo")}</th>
                <th>{t("reportes.nombre")}</th>
                <th className="text-end" style={{ width: 140 }}>{t("reportes.balAnterior")}</th>
                <th className="text-end" style={{ width: 130 }}>{t("reportes.debitos")}</th>
                <th className="text-end" style={{ width: 130 }}>{t("reportes.creditos")}</th>
                <th className="text-end" style={{ width: 130 }}>{t("reportes.saldoFinal")}</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <FilaCargando colSpan={6} msg={t("reportes.calculandoBalance")}/>
                : visibles.length === 0
                  ? <FilaVacia colSpan={6} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
                  : visibles.map(f => <Fila key={f.id} fila={f} collapsed={collapsed}
                      onToggle={id => setCollapsed(prev => toggle(prev, id))}/>)}
            </tbody>
            {!loading && data && (
              <tfoot>
                <tr style={{ background: "rgba(249,115,22,0.08)", borderTop: "2px solid rgba(249,115,22,0.4)" }}>
                  <td colSpan={2} style={{ fontWeight: 700, color: "#f97316", padding: "10px 12px", fontSize: "0.87rem" }}>
                    {t("reportes.totales")}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--text-secondary)", padding: "10px 12px" }}>
                    {fmtCRC(totalBA)}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700, color: "#60a5fa", padding: "10px 12px" }}>
                    {fmtCRC(totalDeb)}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700, color: "#c084fc", padding: "10px 12px" }}>
                    {fmtCRC(totalCred)}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700,
                    color: bal ? "#4ade80" : "#f87171", padding: "10px 12px" }}>
                    {bal
                      ? <><i className="fa-solid fa-check" style={{ marginRight: 4 }}/>{t("reportes.balanceadoCheck")}</>
                      : fmtCRC(Math.abs(diferencia))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
}

// ── Shared helpers (exported for reuse) ──────────────────────────────────────
export function buildChildrenOf(filas) {
  const m = {};
  for (const f of filas) {
    if (f.padre_id != null) { if (!m[f.padre_id]) m[f.padre_id] = []; m[f.padre_id].push(f.id); }
  }
  return m;
}
export function buildHidden(filas, childrenOf, collapsed) {
  const h = new Set();
  for (const f of filas) {
    if (collapsed.has(f.id)) {
      const q = [...(childrenOf[f.id] ?? [])];
      while (q.length) { const id = q.shift(); h.add(id); q.push(...(childrenOf[id] ?? [])); }
    }
  }
  return h;
}
export function toggle(prev, id) {
  const next = new Set(prev);
  next.has(id) ? next.delete(id) : next.add(id);
  return next;
}

// ── Shared UI components ─────────────────────────────────────────────────────
export function FiltrosBarra({ fechaDesde, setFechaDesde, fechaHasta, setFechaHasta,
    soloConMov, setSoloConMov, onActualizar, loading, extraButtons }) {
  const { t } = useTranslation();
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 8,
      padding: "14px 18px", marginBottom: 16,
      display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" }}>
      {fechaDesde !== undefined && (
        <div>
          <label style={{ fontSize: "0.78rem", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
            {t("reportes.desde")}
          </label>
          <input type="date" value={fechaDesde} onChange={e => {
            const desde = e.target.value;
            setFechaDesde(desde);
            if (desde && setFechaHasta) {
              const [y, m] = desde.split("-").map(Number);
              const finMes = new Date(y, m, 0);
              const dd = String(finMes.getDate()).padStart(2, "0");
              const mm = String(m).padStart(2, "0");
              setFechaHasta(`${y}-${mm}-${dd}`);
            }
          }}
            style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)", borderRadius: 6,
              color: "var(--text-primary)", padding: "6px 10px", fontSize: "0.85rem", colorScheme: "light dark" }}/>
        </div>
      )}
      {fechaHasta !== undefined && (
        <div>
          <label style={{ fontSize: "0.78rem", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
            {fechaDesde !== undefined ? t("reportes.hasta") : t("reportes.fechaCorte")}
          </label>
          <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
            style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)", borderRadius: 6,
              color: "var(--text-primary)", padding: "6px 10px", fontSize: "0.85rem", colorScheme: "light dark" }}/>
        </div>
      )}
      {soloConMov !== undefined && (
        <label style={{ display: "flex", alignItems: "center", gap: 6,
          fontSize: "0.83rem", color: "var(--text-secondary)", cursor: "pointer" }}>
          <input type="checkbox" checked={soloConMov} onChange={e => setSoloConMov(e.target.checked)}
            style={{ accentColor: "#f97316" }}/>
          {t("reportes.ocultarSinMovimiento")}
        </label>
      )}
      <button onClick={onActualizar} disabled={loading}
        style={{ padding: "7px 16px", background: "#f97316", border: "none",
          borderRadius: 6, color: "#fff", fontWeight: 600, cursor: "pointer",
          fontSize: "0.85rem", opacity: loading ? 0.7 : 1 }}>
        {loading
          ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("reportes.calculando")}</>
          : <><i className="fa-solid fa-rotate-right"/> {t("reportes.actualizar")}</>}
      </button>
      {extraButtons}
    </div>
  );
}

export function CardsResumen({ cards }) {
  const { fmtCRC } = useCurrency();

  function fmtCard(card) {
    const v     = Number(card.value ?? 0);
    const isNeg = String(card.prefix || "").startsWith("-");
    return `${isNeg ? "-" : ""}${fmtCRC(v)}`;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
      {cards.map(card => (
        <div key={card.label} style={{ background: "var(--bg-card)", border: `1px solid ${card.color}30`,
          borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 9, flexShrink: 0,
            background: `${card.color}15`, border: `1px solid ${card.color}30`,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className={`fa-solid ${card.icon}`} style={{ color: card.color, fontSize: "0.95rem" }}/>
          </div>
          <div>
            <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "0.68rem", fontWeight: 600,
              textTransform: "uppercase", letterSpacing: "0.4px" }}>{card.label}</p>
            <p style={{ margin: "2px 0 0", color: "var(--text-primary)", fontSize: "0.92rem",
              fontWeight: 700, fontFamily: "monospace", whiteSpace: "nowrap" }}>
              {fmtCard(card)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function TableHeader({ count, loading, onColapsar, onExpandir }) {
  const { t } = useTranslation();
  return (
    <div className={`${invoiceStyles["table-header"]} d-flex justify-content-between align-items-center`}>
      <span style={{ fontSize: "0.81rem", color: "var(--text-secondary)" }}>
        {loading ? t("reportes.calculando") : `${count} ${t("reportes.cuentas")}`}
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={onColapsar} style={{ padding: "3px 10px", background: "transparent",
          border: "1px solid var(--border-card)", borderRadius: 5,
          color: "var(--text-secondary)", fontSize: "0.74rem", cursor: "pointer" }}>
          {t("reportes.colapsarTodo")}
        </button>
        <button onClick={onExpandir} style={{ padding: "3px 10px", background: "transparent",
          border: "1px solid var(--border-card)", borderRadius: 5,
          color: "var(--text-secondary)", fontSize: "0.74rem", cursor: "pointer" }}>
          {t("reportes.expandirTodo")}
        </button>
      </div>
    </div>
  );
}

export function FilaCargando({ colSpan, msg }) {
  const { t } = useTranslation();
  return (
    <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: 48, color: "var(--text-secondary)" }}>
      <i className="fa-solid fa-circle-notch fa-spin"
         style={{ color: "#f97316", fontSize: "1.4rem", display: "block", marginBottom: 10 }}/>
      {msg || t("reportes.calculando")}
    </td></tr>
  );
}

export function FilaVacia({ colSpan, soloConMov, onMostrarTodas }) {
  const { t } = useTranslation();
  return (
    <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
      {t("reportes.sinDatos")}
      {soloConMov && onMostrarTodas && (
        <button onClick={onMostrarTodas} style={{ marginLeft: 8, background: "none", border: "none",
          color: "#f97316", cursor: "pointer", fontSize: "0.83rem", textDecoration: "underline" }}>
          {t("reportes.mostrarTodas")}
        </button>
      )}
    </td></tr>
  );
}

export function FilaArbol({ fila, collapsed, onToggle, colorFn, valorFn, bgFn, colSpan = 1 }) {
  const indent = NIVEL_INDENT[fila.nivel] ?? 0;
  const rowBg  = bgFn ? bgFn(fila) : NIVEL_BG[fila.nivel];
  return (
    <tr style={{ background: rowBg, borderBottom: "1px solid var(--border-card)" }}>
      <td style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "var(--text-muted)",
        whiteSpace: "nowrap", padding: "6px 10px" }}>
        {fila.codigo}
      </td>
      <td style={{ padding: "6px 8px", paddingLeft: indent + 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {fila.tiene_hijos ? (
            <button onClick={() => onToggle(fila.id)}
              style={{ background: "none", border: "none", cursor: "pointer",
                color: "var(--text-muted)", padding: 0, fontSize: "0.65rem", width: 14, flexShrink: 0 }}>
              <i className={`fa-solid fa-chevron-${collapsed.has(fila.id) ? "right" : "down"}`}/>
            </button>
          ) : <span style={{ width: 14, flexShrink: 0 }}/>}
          <span style={{ fontWeight: NIVEL_WEIGHT[fila.nivel],
            color: colorFn ? colorFn(fila) : NIVEL_COLOR[fila.nivel],
            fontSize: fila.nivel === 1 ? "0.88rem" : "0.83rem" }}>
            {fila.nombre}
          </span>
        </div>
      </td>
      <td className="text-end" style={{ fontFamily: "monospace", fontSize: "0.83rem",
        padding: "6px 14px", fontWeight: fila.nivel <= 2 ? 700 : 400,
        color: valorFn ? valorFn(fila).color : NIVEL_COLOR[fila.nivel] }}>
        {valorFn ? valorFn(fila).text : ""}
      </td>
    </tr>
  );
}
